// T1 (Readout) Simulation (2D tabs): the desktop's panel - Hamiltonian, readout rates, protocol and temperature,
// simulation settings, the load-time sweep and the Γ_eff field sweep - and its two plots.
import { Alert, Button, Card, Grid, Group, Select, SimpleGrid, Text, TextInput, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { ReadoutPlots, type T1ResultJson } from "../components/DevicePlots";
import { ErrorBox } from "../components/Errors";
import { latestRun, limitError, PreviousRuns, RunCard } from "../components/RunCard";
import { patch, post, type Job, type ProjectDetail } from "../lib/api";
import { checkT1, formatSeconds, freshT1Form, type DeviceForms, type T1Form } from "../lib/device";
import { useLiveJob, useMe } from "../lib/hooks";

const KIND = "transport.t1sim";
type Scalar = Exclude<keyof T1Form, "tab" | "values" | "rates" | "protocol">;

const g4 = (v: number | null | undefined) => (v == null ? "" : String(Number(v.toPrecision(4))));

export function ReadoutPanel({ project, tab, forms, header }: {
  project: ProjectDetail; tab: string; forms: DeviceForms; header: ReactNode;
}) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const F = forms.t1sim;
  const saved = (project.settings?.t1sim as Record<string, T1Form> | undefined)?.[tab];
  const [form, setForm] = useState<T1Form>(() => saved ?? freshT1Form(forms, tab));
  const [debounced] = useDebouncedValue(form, 300);
  const check = useQuery({ queryKey: ["t1-check", debounced], queryFn: () => checkT1(debounced), placeholderData: (prev) => prev });
  const c = check.data;

  const latest = latestRun(project, KIND);
  const [jobId, setJobId] = useState<string | undefined>(latest?.id);
  useEffect(() => { if (!jobId && latest) setJobId(latest.id); }, [latest, jobId]);
  const { job } = useLiveJob(jobId);
  const busy = job && (job.status === "queued" || job.status === "running");
  const result = job?.status === "succeeded" ? (job.result as T1ResultJson & { tab: string } | undefined) : undefined;

  const run = useMutation({
    mutationFn: async () => {
      const fresh = await checkT1(form);
      if (!fresh.ok) throw new Error("Fix the fields marked in red first.");
      return post<Job>("/jobs", { kind: KIND, project_id: project.id, params: form });
    },
    onSuccess: (j) => {
      setJobId(j.id);
      qc.invalidateQueries({ queryKey: ["project", project.id] });
      qc.invalidateQueries({ queryKey: ["me"] });
      const all = (project.settings?.t1sim as Record<string, T1Form> | undefined) ?? {};
      patch(`/projects/${project.id}`, { settings: { ...project.settings, t1sim: { ...all, [tab]: form }, transport_view: { tab, mode: "t1sim" } } })
        .then(() => qc.invalidateQueries({ queryKey: ["project", project.id] })).catch(() => undefined);
    },
  });

  const err = (name: string) => c?.errors[name];
  const field = (name: Scalar, label: string, opts: { tip?: string; placeholder?: string } = {}) => (
    <Tooltip label={opts.tip} disabled={!opts.tip} multiline w={300} withArrow openDelay={300}>
      <TextInput size="xs" label={label} value={form[name]} error={err(name)} placeholder={opts.placeholder}
        onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, [name]: v })); }} />
    </Tooltip>
  );
  const auto = c?.ok ? c.auto : undefined;
  const maxM = Number(me?.limits["transport.t1_max_msteps"] ?? 0);

  return (
    <Grid gutter="md">
      <Grid.Col span={{ base: 12, lg: 5 }}>
        <Card withBorder>
          {header}
          <Text size="sm" fs="italic" c="dimmed" mt="sm">
            Elzerman-readout Monte Carlo simulation of T1 as a composite of gamma_s, gamma_v and gamma_k for the device defined
            by the Hamiltonian below. Region I / III fields and the field-sweep maximum are placed automatically when left blank.
          </Text>

          <Section title="Hamiltonian (N = 1)">
            {F.params.map((p) => (
              <TextInput key={p.name} size="xs" label={p.label} value={form.values[p.name] ?? ""} error={err(p.name)}
                onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, values: { ...f.values, [p.name]: v } })); }} />
            ))}
          </Section>
          <Section title="Readout rates (Hz)">
            {F.rates.map((p) => (
              <TextInput key={p.name} size="xs" label={p.label} value={form.rates[p.name] ?? ""} error={err(p.name)}
                onChange={(e) => { const v = e.currentTarget.value; setForm((f) => ({ ...f, rates: { ...f.rates, [p.name]: v } })); }} />
            ))}
          </Section>
          <Section title="Protocol & temperature">
            <Select size="xs" label="Protocol [l, r]" data={F.protocols} value={form.protocol} allowDeselect={false} error={err("protocol")}
              onChange={(v) => v && setForm((f) => ({ ...f, protocol: v }))} />
            {field("temp_mK", "Temperature (mK)")}
          </Section>
          <Section title="Simulation settings">
            {field("n_runs", "MCMC runs per point")}
            {field("n_time_steps", "Load-phase time steps")}
            {field("read_time", "Read time (s)")}
            {field("read_dt", "Read time step (s)")}
            {field("seed", "Random seed (optional)", { tip: "Blank = a fresh random draw every run." })}
          </Section>
          <Section title="Load-time sweep plot: fields and load times">
            {field("b1", "Region I field b1 (T)", { tip: "Out-of-plane field for the Region I curve. Blank = automatic placement.",
              placeholder: auto?.b1 != null ? `auto: ${g4(auto.b1)}` : "auto" })}
            {field("b3", "Region III field b3 (T)", { tip: "Out-of-plane field for the Region III curve. Blank = automatic placement.",
              placeholder: auto?.b3 != null ? `auto: ${g4(auto.b3)}` : "auto" })}
            {field("load_t_min", "Min load time (s)")}
            {field("load_t_max", "Max load time (s)")}
            {field("load_t_n", "Load-time points (log-spaced)")}
          </Section>
          <Section title="Γ_eff plot: field sweep">
            {field("b_scan_max", "Field-sweep maximum (T)", { tip: "Highest out-of-plane field swept. Blank = automatic (2 × the anti-crossing field Bt1).",
              placeholder: auto?.b_scan_max != null ? `auto: ${g4(auto.b_scan_max)}` : "auto" })}
            {field("b_scan_n", "Field-sweep points")}
          </Section>

          {auto && (
            <Text size="xs" c="dimmed" mt="sm">
              Bt1 (anti-crossing) = {auto.bt1 != null ? `${g4(auto.bt1)} T` : "none (gv = 0)"}.
              {auto.region_note ? ` ${auto.region_note}` : ""}
            </Text>
          )}
          {c?.ok && c.msteps != null && (
            <Text size="xs" mt={4} c={c.limit ? "orange" : "dimmed"}>
              Size: {Math.ceil(c.msteps).toLocaleString()} million Monte Carlo steps (your plan: up to {maxM.toLocaleString()});
              roughly {formatSeconds(c.estimate_s ?? 0)} on the server.
            </Text>
          )}
          {c?.general_error && <Alert color="red" mt="sm" p="xs"><Text size="sm">{c.general_error}</Text></Alert>}
          {c?.limit && <ErrorBox error={limitError(c.limit)} />}
          <ErrorBox error={run.error} />
          <Group mt="md" justify="space-between">
            <Button variant="subtle" size="xs" onClick={() => setForm(freshT1Form(forms, tab))}>Reset to defaults</Button>
            <Button onClick={() => run.mutate()} loading={run.isPending} disabled={!c?.ok || !!c?.limit || !!busy}>
              {busy ? "Running…" : "Run T1 simulation"}
            </Button>
          </Group>
        </Card>
      </Grid.Col>

      <Grid.Col span={{ base: 12, lg: 7 }}>
        <RunCard job={job} exportName={result ? `t1sim_${result.tab}_${result.protocol}_export.zip` : undefined}
          empty={<Text c="dimmed">Set the parameters and press Run T1 simulation. The two plots appear here.</Text>}>
          {result && <ReadoutPlots result={result} />}
        </RunCard>
        <PreviousRuns project={project} kind={KIND} current={jobId} onOpen={setJobId}
          describe={(p) => `${String(p.tab ?? "").toUpperCase()} · ${p.protocol ?? ""} · ${p.temp_mK ?? ""} mK · ${p.n_runs ?? ""} runs`} />
      </Grid.Col>
    </Grid>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <Text fw={600} size="sm" mt="md" mb={4}>{title}</Text>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing="xs">{children}</SimpleGrid>
    </div>
  );
}
